import React, { ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './Patterns.css';

export interface ModalProps {
  show: boolean;
  onHide: () => void;
  title: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Optional className for the outer dialog (rare). */
  dialogClassName?: string;
  children: ReactNode;
}

const Body: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="pat-modal__body">{children}</div>
);

const Footer: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="pat-modal__footer">{children}</div>
);

interface ModalCompound extends React.FC<ModalProps> {
  Body: typeof Body;
  Footer: typeof Footer;
}

/**
 * pat-* Modal primitive. Compound API mirrors react-bootstrap's Modal so the
 * Phase C2 migration is mostly an import swap:
 *   <Modal show onHide={...} title="..." size="lg">
 *     <Modal.Body>...</Modal.Body>
 *     <Modal.Footer>...</Modal.Footer>
 *   </Modal>
 *
 * Behaviour:
 *  - Renders to document.body via React portal.
 *  - Focus trap entry: focuses the first focusable element in the dialog.
 *  - Restores focus to the previously-focused element on close.
 *  - Locks <body> scroll while open.
 *  - Escape and backdrop click both fire onHide.
 *  - The header renders an X close button labelled via patterns.modal.closeLabel.
 */
const Modal: ModalCompound = ({ show, onHide, title, size = 'md', dialogClassName, children }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Body scroll lock — restore the previous overflow value on close so
  // nested usage (or external libs setting overflow) is preserved.
  useEffect(() => {
    if (!show) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [show]);

  // Focus trap entry + restore previous focus on close.
  useEffect(() => {
    if (!show) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    (focusable?.[0] ?? dialogRef.current)?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
  }, [show]);

  // Escape to close.
  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onHide();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show, onHide]);

  // Tab trap — cycle focus within the dialog per ARIA APG dialog pattern.
  useEffect(() => {
    if (!show) return;
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) ?? []
      );
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onTab);
    return () => document.removeEventListener('keydown', onTab);
  }, [show]);

  if (!show) return null;

  const node = (
    <div className="pat-modal" data-testid="modal">
      <div className="pat-modal__backdrop" onClick={onHide} aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`pat-modal__dialog pat-modal__dialog--${size}${dialogClassName ? ` ${dialogClassName}` : ''}`}
      >
        <header className="pat-modal__header">
          <h2 id={titleId} className="pat-modal__title">{title}</h2>
          <button
            type="button"
            className="pat-modal__close"
            onClick={onHide}
            aria-label={t('patterns.modal.closeLabel')}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(node, document.body) : node;
};

Modal.Body = Body;
Modal.Footer = Footer;

export default Modal;
